import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Stethoscope, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * Wird nach erfolgreichem Check-in eines Insurance-Patienten geöffnet.
 * Zeigt die aktive Verordnung + vorausgewählte Tarifpositionen und bucht
 * bestätigte Positionen als Sitzung auf die Verordnung.
 */
const PhysioCheckinDialog = ({ state, onClose }) => {
  const navigate = useNavigate();
  const data = state?.data || {};
  const rx = data.prescription;
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (state?.open) {
      const sel = {};
      (data.suggested_positions || []).forEach(p => { sel[p.tariff_position_id] = 1; });
      setLines((data.tariff_positions || []).map(t => ({
        tariff_position_id: t.id, code: t.code, label: t.label,
        checked: !!sel[t.id], quantity: sel[t.id] || 1,
      })));
    }
  }, [state]); // eslint-disable-line

  const toggle = (id) => setLines(ls => ls.map(l => l.tariff_position_id === id ? { ...l, checked: !l.checked } : l));
  const setQty = (id, q) => setLines(ls => ls.map(l => l.tariff_position_id === id ? { ...l, quantity: q } : l));

  const book = async () => {
    if (!rx) return;
    const selected = lines.filter(l => l.checked).map(l => ({ tariff_position_id: l.tariff_position_id, quantity: parseFloat(l.quantity) || 1 }));
    setSaving(true);
    try {
      await axios.post(`${API}/physio-billing/prescriptions/${rx.id}/sessions`, { lines: selected }, { headers: authHeaders() });
      toast.success('Leistung auf Verordnung gebucht');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Buchung fehlgeschlagen');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!state?.open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="physio-checkin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-emerald-600" />Physio-Leistung erfassen</DialogTitle>
        </DialogHeader>
        {!rx && (
          <div className="text-sm space-y-3">
            <div className="flex items-center gap-2 text-amber-700"><AlertTriangle className="h-4 w-4" />Keine aktive Verordnung gefunden.</div>
            <Button variant="outline" onClick={() => { onClose(); navigate('/physio-billing'); }} data-testid="physio-checkin-goto">Zur Physio-Abrechnung</Button>
          </div>
        )}
        {rx && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md bg-muted p-2">
              <b>{rx.diagnosis || 'Verordnung'}</b> · {rx.physician_name || '—'} · Serie {rx.series_no}
              <div className="text-muted-foreground">Sitzungen {rx.sessions_used}/{rx.sessions_granted} · verbleibend {rx.sessions_remaining}</div>
            </div>
            {(rx.warnings || []).map((w, i) => (
              <div key={i} className="text-xs flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" />{w}</div>
            ))}
            <div className="space-y-1">
              {lines.map(l => (
                <label key={l.tariff_position_id} className="flex items-center gap-2 py-1" data-testid={`physio-checkin-pos-${l.code}`}>
                  <input type="checkbox" checked={l.checked} onChange={() => toggle(l.tariff_position_id)} className="h-4 w-4" />
                  <span className="flex-1">{l.code} · {l.label}</span>
                  {l.checked && <Input type="number" className="w-16 h-8" value={l.quantity} onChange={e => setQty(l.tariff_position_id, e.target.value)} />}
                </label>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Überspringen</Button>
          {rx && <Button onClick={book} disabled={saving} data-testid="physio-checkin-book">Sitzung buchen</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhysioCheckinDialog;
